/* component-collection-carousel.js
 * Collection-list carousel enhancement layer (Slice 30 Phase C).
 * Self-loaded via <script defer> from sections/collection-list.liquid
 * when section.settings.layout_type == 'carousel'.
 *
 * Architecture: CSS scroll-snap is the foundational primitive (works without JS for
 * touch/scroll/keyboard arrow-key scrolling). This file enhances with prev/next
 * button navigation, dot-indicator click-to-slide, and active-slide tracking via
 * IntersectionObserver.
 *
 * Progressive-enhancement note: unlike the featured-collection carousel (which knows
 * the block count in Liquid), the collection-list section renders its items through
 * content_for "blocks" (S20) — the section cannot introspect the block count. So this
 * engine derives slides from the track's <li> children at runtime, adds slide indices
 * + ARIA, and generates the dot pager from the discovered count.
 *
 * Per-carousel state lives in the initCarousel(el) closure — multiple carousels on
 * one page operate independently. Compound carousel ID (section.id + '-cl') prevents
 * collision when two collection-list sections appear on the same page.
 *
 * IIFE-dedup-guarded per Rule J12 — safe to inject multiple times via separate
 * <script> tags (browser dedupes by URL anyway, but the guard is belt-and-braces).
 *
 * Reduced-motion: detected once at init via matchMedia; programmatic scroll calls
 * use 'auto' instead of 'smooth' when set. Mirrors the CSS-side
 * @media (prefers-reduced-motion) override of scroll-behavior.
 *
 * Shopify customizer hot-reload: shopify:section:load event re-inits any carousel
 * inside the reloaded section. Old DOM is replaced by Shopify; old listeners + IO
 * GC with their closure.
 */
(function () {
  'use strict';

  if (window.__BLOCKDEDUP_COLLECTION_CAROUSEL__) {
    return;
  }
  window.__BLOCKDEDUP_COLLECTION_CAROUSEL__ = true;

  function enhanceSlides(slides) {
    var total = slides.length;
    slides.forEach(function (slide, i) {
      slide.setAttribute('data-carousel-slide-index', i);
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', (i + 1) + ' / ' + total);
    });
  }

  function buildDots(dotsOl, slideCount, scrollToSlide) {
    dotsOl.innerHTML = '';
    for (var i = 0; i < slideCount; i++) {
      var li = document.createElement('li');
      li.className = 'section-collection-list__dot-item';
      li.setAttribute('role', 'presentation');

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'section-collection-list__dot';
      if (i === 0) btn.classList.add('section-collection-list__dot--active');
      btn.setAttribute('data-carousel-dot', '');
      btn.setAttribute('data-carousel-dot-index', i);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.setAttribute('tabindex', i === 0 ? '0' : '-1');
      btn.setAttribute('aria-label', (i + 1) + ' / ' + slideCount);
      btn.addEventListener('click', (function (idx) {
        return function () { scrollToSlide(idx); };
      })(i));

      li.appendChild(btn);
      dotsOl.appendChild(li);
    }
  }

  function initCarousel(carouselEl) {
    var trackEl = carouselEl.querySelector('[data-carousel-track]');
    var prevBtn = carouselEl.querySelector('[data-carousel-prev]');
    var nextBtn = carouselEl.querySelector('[data-carousel-next]');
    var dotsOl = carouselEl.querySelector('[data-carousel-dots]');
    var trackInner = carouselEl.querySelector('.section-collection-list__track');

    var slidesNodeList = trackInner
      ? Array.prototype.slice.call(trackInner.children)
      : [];

    if (!trackEl || !prevBtn || !nextBtn || slidesNodeList.length === 0) {
      return;
    }

    var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth';

    function getSlideStep() {
      var firstSlide = slidesNodeList[0];
      if (!firstSlide || !trackInner) return 0;
      var slideWidth = firstSlide.offsetWidth;
      var trackStyle = window.getComputedStyle(trackInner);
      var gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      return slideWidth + gap;
    }

    function scrollByOneSlide(direction) {
      var step = getSlideStep();
      if (step === 0) return;
      trackEl.scrollBy({
        left: direction * step,
        behavior: scrollBehavior
      });
    }

    function scrollToSlide(idx) {
      var targetSlide = slidesNodeList[idx];
      if (!targetSlide) return;
      var trackBox = trackEl.getBoundingClientRect();
      var slideBox = targetSlide.getBoundingClientRect();
      var offset = slideBox.left - trackBox.left + trackEl.scrollLeft;
      trackEl.scrollTo({ left: offset, behavior: scrollBehavior });
    }

    // Slide index + ARIA (JS-derived — the section can't know the block count, S20).
    enhanceSlides(slidesNodeList);
    // Dot pager generated from the discovered slide count (only when the merchant enabled dots).
    if (dotsOl) {
      buildDots(dotsOl, slidesNodeList.length, scrollToSlide);
    }

    var dotsNodeList = dotsOl ? dotsOl.querySelectorAll('[data-carousel-dot]') : [];

    function setActiveDot(idx) {
      if (dotsNodeList.length === 0) return;
      dotsNodeList.forEach(function (dot, i) {
        var isActive = i === idx;
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        dot.setAttribute('tabindex', isActive ? '0' : '-1');
        dot.classList.toggle('section-collection-list__dot--active', isActive);
      });
    }

    function updateEdgeStates() {
      var atStart = trackEl.scrollLeft <= 1;
      var atEnd = trackEl.scrollLeft + trackEl.clientWidth >= trackEl.scrollWidth - 1;
      prevBtn.disabled = atStart;
      nextBtn.disabled = atEnd;
    }

    function handleIntersect(entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var idx = parseInt(entry.target.getAttribute('data-carousel-slide-index'), 10);
          if (!isNaN(idx)) setActiveDot(idx);
        }
      });
    }

    prevBtn.addEventListener('click', function () { scrollByOneSlide(-1); });
    nextBtn.addEventListener('click', function () { scrollByOneSlide(1); });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(handleIntersect, {
        root: trackEl,
        threshold: 0.6
      });
      slidesNodeList.forEach(function (slide) { io.observe(slide); });
    }

    trackEl.addEventListener('scroll', updateEdgeStates, { passive: true });
    updateEdgeStates();
  }

  function initAll(scopeEl) {
    var root = scopeEl || document;
    var carousels = root.querySelectorAll('[data-collection-carousel]');
    carousels.forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) initAll(event.target);
  });
})();
